/**
 * The editing tools, at the head of the floating canvas palette: select and
 * connector, then undo and redo, then whatever the view adds (export). Icons
 * only, each named to assistive technology and in its tooltip, which gives
 * the shortcut too. Shared by the design workspace
 * and a block definition's own view, and drawn inside `ViewControls`.
 */
import type { ReactNode } from "react";
import type { EditorAction, EditorState } from "../editor";
import { canRedo, canUndo } from "../editor";
import { ConnectorToolIcon, NoteToolIcon, RedoIcon, SelectToolIcon, UndoIcon } from "../icons";

/** Undo and redo of something other than the drawing, such as a script's edits. */
export interface History {
  canUndo: boolean;
  canRedo: boolean;
  undo: () => void;
  redo: () => void;
}

export function EditorTools({
  state,
  dispatch,
  children,
  notes = false,
  drawing = true,
  history,
}: {
  /** Offer the note tool: a design has notes; a block definition does not. */
  notes?: boolean;
  /**
   * The drawing is being edited, so the connector (and note) tools apply.
   * False where it is only looked at, as in Scripts: select stays, alone.
   */
  drawing?: boolean;
  /** What undo and redo step through, where it is not the drawing's own history. */
  history?: History;
  state: EditorState;
  dispatch: (action: EditorAction) => void;
  /** More controls after the history, such as export. */
  children?: ReactNode;
}) {
  const undoable = history ?? {
    canUndo: canUndo(state),
    canRedo: canRedo(state),
    undo: () => dispatch({ type: "UNDO" }),
    redo: () => dispatch({ type: "REDO" }),
  };
  // Only looking: whatever tool was left on, it is select that applies.
  const current = drawing ? state.tool : "select";
  const tool = (id: EditorState["tool"], label: string, key: string, glyph: ReactNode) => (
    <button
      type="button"
      className={`ws-tool${current === id ? " ws-view-on" : ""}`}
      aria-pressed={current === id}
      aria-label={label}
      aria-keyshortcuts={key}
      title={`${label}  ${key}`}
      onClick={() => dispatch({ type: "SET_TOOL", tool: id })}
    >
      {glyph}
    </button>
  );
  return (
    <>
      <div className="ws-view-group" role="group" aria-label="Tools">
        {tool("select", "Select", "V", <SelectToolIcon />)}
        {drawing && tool("connect", "Connector", "C", <ConnectorToolIcon />)}
        {drawing && notes && tool("note", "Note", "N", <NoteToolIcon />)}
      </div>
      <span className="ws-view-sep" aria-hidden="true" />
      {/* Drawn as U-turns: bare curved arrows next to zoom read as rotation. */}
      <div className="ws-view-group" role="group" aria-label="History">
        <button
          type="button"
          aria-label="Undo"
          aria-keyshortcuts="Meta+Z Control+Z"
          title="Undo  ⌘Z"
          disabled={!undoable.canUndo}
          onClick={undoable.undo}
        >
          <UndoIcon />
        </button>
        <button
          type="button"
          aria-label="Redo"
          aria-keyshortcuts="Meta+Shift+Z Control+Shift+Z"
          title="Redo  ⇧⌘Z"
          disabled={!undoable.canRedo}
          onClick={undoable.redo}
        >
          <RedoIcon />
        </button>
      </div>
      {children && (
        <>
          <span className="ws-view-sep" aria-hidden="true" />
          {children}
        </>
      )}
    </>
  );
}
